import React, { useEffect, useState } from "react";
import {
  Badge,
  Breadcrumbs,
  Button,
  Card,
  PageHeader,
  Pagination,
  Table,
} from "@opendate-app/setlist";

import SmsPreview from "./SmsPreview";
import WorkflowDetails from "./WorkflowDetails";
import EmailPreview from "./EmailPreview";
import { useCampaignContext } from "./CampaignContainer";
import { toastError } from "../../../shared/toastHelper";
import decodeHtml from "./decodeHtml";
import { listHref } from "../shared/listUrlState";

const axios = require("axios").default;
const qs = require("qs");

const PAGE_SIZE = 25;
const SUCCESS_STATUSES = ["Sent", "Delivered", "Opened", "Clicked", "Converted"];

const containerStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 20,
};

const columnsStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 20,
  alignItems: "flex-start",
};

const columnHeaderStyle = {
  margin: "0 0 12px",
  fontSize: 14,
  fontWeight: 600,
  color: "var(--sl-text-primary)",
};

const previewStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 20,
  color: "var(--sl-text-secondary)",
};

const statusColor = (label) => {
  if (SUCCESS_STATUSES.includes(label)) return "success";
  if (label === "Bounced") return "warning";
  return label ? "error" : "gray";
};

const link = (path, text, newTab) => (
  path ? <a href={path} target={newTab ? "_blank" : undefined} rel={newTab ? "noreferrer" : undefined} style={{ color: "var(--sl-text-brand)" }}>{text}</a> : text
);

const MESSAGE_COLUMNS = [
  { key: "created_at", header: "Date Sent", sortable: true, render: (row) => row.sent_at || "—" },
  { key: "subject", header: "Subject", primary: true, render: (row) => link(row.subject_path, decodeHtml(row.subject_text), true) },
  { key: "recipient", header: "Recipient", render: (row) => link(row.recipient_path, decodeHtml(row.recipient_name)) },
  { key: "status", header: "Status", render: (row) => <Badge size="sm" color={statusColor(row.status_label)}>{row.status_label}</Badge> },
];

const SentMessages = ({ dataSource }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setLoadFailed(false);

    axios.get(dataSource, {
      params: {
        draw: page,
        start: (page - 1) * PAGE_SIZE,
        length: PAGE_SIZE,
        search: { value: "", regex: false },
        columns: { 0: { data: "created_at", searchable: false, orderable: true, search: { value: "", regex: false } } },
        order: { 0: { column: 0, dir: sortDir } },
      },
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "brackets" }),
    }).then(({ data }) => {
      if (ignore) return;
      setRows(data.data || []);
      setTotal(data.recordsTotal || 0);
    }).catch(() => {
      if (ignore) return;
      setLoadFailed(true);
      toastError("Could not load sent messages, please try again");
    }).finally(() => {
      if (!ignore) setLoading(false);
    });

    return () => { ignore = true; };
  }, [dataSource, page, sortDir]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Card padding="none">
      <Table
        columns={MESSAGE_COLUMNS}
        data={rows}
        getRowKey={(row) => row.id}
        loading={loading}
        loadingLabel="Loading sent messages"
        error={loadFailed ? "Could not load sent messages, please try again." : null}
        sort={{ key: "created_at", direction: sortDir }}
        onSortChange={(_key, direction) => {
          setSortDir(direction);
          setPage(1);
        }}
        stackBelow={640}
      />
      {totalPages > 1 && (
        <div className="px-4 py-3 border-top">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </Card>
  );
};

const CampaignShow = () => {
  const {
    csrfToken,
    team,
    venueOwnership,
    campaign,
    ahoyMessagesDataTableProps,
    previouslySent,
  } = useCampaignContext();

  const indexPath = `/teams/${team.id}/campaigns`;

  const startCampaign = () => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.put(`${indexPath}/${campaign.id}`, { state: "active" })
      .then(() => {
        window.location.href = listHref(indexPath);
      })
      .catch(_error => toastError("Could not start automation, please try again"));
  };

  return (
    <div style={containerStyle}>
      <PageHeader
        breadcrumbs={(
          <Breadcrumbs
            items={[
              { label: "Automations", href: listHref(indexPath) },
              { label: venueOwnership.venueable.name },
              { label: campaign.campaign_template.name },
            ]}
          />
        )}
        back={{ label: "Automations", href: listHref(indexPath) }}
        title={campaign.campaign_template.name}
        supportingText={campaign.campaign_template.description}
        actions={(
          campaign.state === "available" ? (
            <Button variant="primary" onClick={startCampaign}>Start Automation</Button>
          ) : null
        )}
      />
      <div style={columnsStyle}>
        <div style={{ flex: "1 1 280px", minWidth: 0 }}>
          <h3 style={columnHeaderStyle}>Details</h3>
          <WorkflowDetails />
        </div>
        <div style={{ flex: "2 1 480px", minWidth: 0 }}>
          <h3 style={columnHeaderStyle}>Preview</h3>
          <Card>
            <div style={previewStyle}>
              {campaign.campaign_actions.length > 0 ? (
                campaign.campaign_actions.map((action) => (
                  action.type === "CampaignActions::Twilio" ? (
                    <SmsPreview key={action.id} action={action} />
                  ) : (
                    <EmailPreview key={action.id} action={action} />
                  )
                ))
              ) : (
                <div style={{ fontSize: 16 }}>Data Collection Only</div>
              )}
            </div>
          </Card>
        </div>
      </div>
      {previouslySent && (
        <div>
          <h3 style={columnHeaderStyle}>Sent messages</h3>
          <SentMessages dataSource={ahoyMessagesDataTableProps.dataSource} />
        </div>
      )}
    </div>
  );
};

export default CampaignShow;
